import { parseArgs } from "../../features/draft/utils/parseArgs";
import { ToolResult } from "../shared/toolResult";
import { CellValue } from "./workbookModel";
import {
  addSheet,
  deleteCells,
  formatRange,
  insertCells,
  readRange,
  selectRange,
  setFormula,
  setNumberFormat,
  setValues,
} from "./rangeTools";

// Dispatch from a tool name to an Excel operation.
//
// Every case validates its own arguments before acting. The model does occasionally
// omit a required field or send a string where an array belongs, and a specific
// complaint -- "values must be an array of rows" -- gets corrected on the next turn,
// where a thrown TypeError just ends it.

interface RangeArgs {
  range?: string;
}

const requireRange = (args: RangeArgs): string | null =>
  typeof args.range === "string" && args.range.trim() ? args.range.trim() : null;

const missing = (field: string, tool: string): ToolResult => ({
  success: false,
  error: `${tool} requires "${field}".`,
});

export async function executeToolCall(name: string, rawArgs: object | string): Promise<ToolResult> {
  const args = parseArgs<Record<string, any>>(rawArgs);

  switch (name) {
    case "read_range": {
      const range = requireRange(args);
      if (!range) return missing("range", name);
      const result = await readRange(range);
      if (!result.success) return result;
      // The values ride back in `detail` because that is what the shared chat layer
      // feeds to the model as the tool's output.
      return {
        success: true,
        detail: JSON.stringify({
          address: result.address,
          values: result.values,
          formulas: result.formulas,
        }),
      };
    }

    case "set_values": {
      const range = requireRange(args);
      if (!range) return missing("range", name);
      if (!Array.isArray(args.values) || !args.values.every(Array.isArray)) {
        return {
          success: false,
          error: `${name} requires "values" as an array of rows, each row an array of cells.`,
        };
      }
      return setValues(range, args.values as CellValue[][]);
    }

    case "set_formula": {
      const range = requireRange(args);
      if (!range) return missing("range", name);
      if (typeof args.formula !== "string" || !args.formula.trim()) {
        return missing("formula", name);
      }
      // A formula written in the {r} notation used to DESCRIBE existing formulas would
      // be written into the cell verbatim and break it. Catch it here with an
      // explanation rather than letting Excel reject an unrecognisable name.
      if (args.formula.includes("{r")) {
        return {
          success: false,
          error:
            "Formulas must be written in ordinary A1 form (=B2*C2). The {r} notation is " +
            "only used to describe existing formulas to you; it is not valid in Excel.",
        };
      }
      return setFormula(range, args.formula.trim());
    }

    case "set_number_format": {
      const range = requireRange(args);
      if (!range) return missing("range", name);
      if (typeof args.format !== "string" || !args.format) return missing("format", name);
      return setNumberFormat(range, args.format);
    }

    case "format_range": {
      const range = requireRange(args);
      if (!range) return missing("range", name);
      const { bold, italic, underline, fontColour, fillColour, horizontalAlignment } = args;
      if (
        [bold, italic, underline, fontColour, fillColour, horizontalAlignment].every(
          (value) => value === undefined
        )
      ) {
        return {
          success: false,
          error: `${name} needs at least one formatting property to change.`,
        };
      }
      return formatRange(range, {
        bold,
        italic,
        underline,
        fontColour,
        fillColour,
        horizontalAlignment,
      });
    }

    case "insert_rows":
    case "insert_columns": {
      const range = requireRange(args);
      if (!range) return missing("range", name);
      return insertCells(range, name === "insert_rows" ? "rows" : "columns");
    }

    case "delete_rows":
    case "delete_columns": {
      const range = requireRange(args);
      if (!range) return missing("range", name);
      return deleteCells(range, name === "delete_rows" ? "rows" : "columns");
    }

    case "add_sheet": {
      if (typeof args.name !== "string" || !args.name.trim()) return missing("name", name);
      return addSheet(args.name.trim());
    }

    case "select_range": {
      const range = requireRange(args);
      if (!range) return missing("range", name);
      return selectRange(range);
    }

    default:
      return { success: false, error: `Unknown tool: ${name}` };
  }
}
